
import { useLeaseEnricher } from "./useLeaseEnricher";
import { LeaseViewModel } from "../ViewModels/LeaseViewModel";

import {
    ActionStatusHelper,
    DomainDetailHook,
    useAppEnum,
    DOMAIN_CONFIGS,
    DomainHookProps,
    useDetailLifeCycle,
    useExportAction,
} from "@/Shared";

import { useDocumentEnricher } from "@/Domains/Document/Logic";
import { useDocument, useLease } from "@/OneLiners";
import { LeaseModel } from "../LeaseModel";


/**
 * useLeaseDetail hook
 *
 * Domain-specific detail hook for Lease entities.
 * Orchestrates domain data enrichment, document handling,
 * deletion workflow, UI feedback (toasts, confirmation),
 * PDF export/generation, and navigation concerns.
 *
 * Responsibilities:
 * - Initialize the LeaseViewModel when data is available.
 * - Enrich the lease with associated documents.
 * - Handle delete operations with access control and confirmation.
 * - Handle PDF export and generation for the current lease.
 * - Manage navigation (layout, error page) based on user permissions.
 * - Expose entity VM, action executor, and access rights to the UI layer.
 *
 * Author: Samano CASTRE
 * Date: March 2, 2026
 *
 * @see DomainHookProps
 * @see DomainDetailHook<LeaseViewModel>
 *
 * @param props DomainHookProps
 * @returns DomainDetailHook<LeaseViewModel>
 */
export function useLeaseDetail(props: DomainHookProps): DomainDetailHook<LeaseViewModel> {
    const { permissionHook, accessRight, uiFeedbackHooks } = props;
    const { domainType, domainLabel } = DOMAIN_CONFIGS.Lease;

    /**
     * Lease-related enums exposed to the UI layer
     */
    const { LeaseEnums } = useAppEnum();

    /**
     * Lease domain CRUD operations and state
     */
    const leaseHook = useLease();

    /**
     * Document domain CRUD operations and state
     */
    const { dataMap: documentMap, stateResponse: documentStateResponse, } = useDocument();


     /**
     * Lease domain data and operations
     * Provided by the lease enricher facade
     */
    const { getStrictEnrichedLease, enricherResponse, } = useLeaseEnricher();


    /**
     * Document domain operations
     * Used to enrich leases with associated documents
     */
    const { enricherResponse: documentEnricherResponse, findByResource } = useDocumentEnricher();


    const { isSucceeded: generateIsSucceed } = ActionStatusHelper.is(leaseHook.generateAction.response.status);

    /**
     * Combined response from lease and document enrichment layers
     */
    const response = {
        status: ActionStatusHelper.combine(
            leaseHook.stateResponse.status,
            documentStateResponse.status,
            enricherResponse.status,
            documentEnricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: leaseHook.stateResponse.error ??
            documentStateResponse.error ??
            enricherResponse.error ??
            documentEnricherResponse.error
    }

    /**
     * Detail life cycle hook
     *
     * Handles:
     * - Entity resolution (queryId / entityId)
     * - ViewModel construction
     * - Post-fetch or post-update handling
     * - Provides public API to the UI layer
     */
    const lifeCycleHook = useDetailLifeCycle({
        response,
        domainType,
        props,
        entityHook: leaseHook,
        buildVM: (currentEntity: LeaseModel, isDisplaying: boolean) => {
            const foundDocumentMap = findByResource(documentMap, domainType, currentEntity.id);
            return new LeaseViewModel({
                lease: getStrictEnrichedLease(currentEntity),
                documentMap: foundDocumentMap,
                viewEnums: LeaseEnums,
                isDisplaying,
                accessRight
            })
        },
        deps: [generateIsSucceed]
    });

    const { entityVM, requestId } = lifeCycleHook;

    const leaseType = entityVM?.model.leaseType;
     const {computeUIActions} = useExportAction<LeaseModel, LeaseViewModel>({
        accessRight : props.accessRight,
        uiFeedbackHooks: props.uiFeedbackHooks,
        entityVM : entityVM,
        domainType,
        domainLabel,
        entityHook: leaseHook,
        supportedDocTypes : leaseType ? [leaseType] : undefined,
        noFormDocTypes : leaseType ? [leaseType] : undefined,
    });
   

    /**
     * Public API exposed to the UI layer.
     */
    return {
        ...lifeCycleHook,
        computeUIActions,
    }
}